import { parse, simplify, type NBTFormat } from 'prismarine-nbt';
import { isBedrockStructure, normalizeBedrockStructure } from './bedrockStructure';
import { blockColor, isAir } from './blockColor';
import { MAX_BLOCKS, MAX_FILE_BYTES, StructureError, asVec3, checkBlockName, checkSize, isRecord, type UnknownRecord } from './common';
import { resolveConnections, resolveShapeConnections } from './connections';
import type { PaletteEntry, StructureBlock, StructureModel } from './types';

function readPalette(root: UnknownRecord): PaletteEntry[] {
  const paletteValue = root.palette ?? (Array.isArray(root.palettes) ? root.palettes[0] : undefined);
  if (!Array.isArray(paletteValue) || paletteValue.length === 0) {
    throw new StructureError('palette（ブロック種類）が見つかりません。Java版の構造ブロック出力を選んでください。');
  }

  return paletteValue.map((value, index) => {
    if (!isRecord(value)) throw new StructureError(`palette[${index}]の形式が不正です。`);
    const rawName = checkBlockName(value.Name ?? value.name, `palette[${index}]のName`);
    const rawProperties = value.Properties ?? value.properties;
    const properties = isRecord(rawProperties)
      ? Object.fromEntries(Object.entries(rawProperties).map(([key, item]) => [key, String(item)]))
      : {};
    const propertyLabel = Object.entries(properties)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, item]) => `${key}=${item}`)
      .join(', ');
    return {
      name: rawName,
      properties,
      label: propertyLabel ? `${rawName} [${propertyLabel}]` : rawName,
      color: blockColor(rawName),
    };
  });
}

export function normalizeStructure(raw: unknown, format: NBTFormat, name = 'structure.nbt'): StructureModel {
  if (!isRecord(raw)) throw new StructureError('NBTのルートがCompoundではありません。');
  const size = asVec3(raw.size, 'size');
  checkSize(size);

  const palette = readPalette(raw);
  if (!Array.isArray(raw.blocks)) throw new StructureError('blocks（配置ブロック）が見つかりません。');
  if (raw.blocks.length > MAX_BLOCKS) {
    throw new StructureError(`ブロック数が上限${MAX_BLOCKS.toLocaleString()}個を超えています。`);
  }

  const blocks: StructureBlock[] = [];
  raw.blocks.forEach((value, index) => {
    if (!isRecord(value)) throw new StructureError(`blocks[${index}]の形式が不正です。`);
    const position = asVec3(value.pos, `blocks[${index}].pos`);
    const state = Number(value.state);
    if (!Number.isInteger(state) || state < 0 || state >= palette.length) {
      throw new StructureError(`blocks[${index}].stateがpaletteの範囲外です。`);
    }
    if (position.some((coordinate, axis) => coordinate < 0 || coordinate >= size[axis])) {
      throw new StructureError(`blocks[${index}].posがsizeの範囲外です。`);
    }
    if (!isAir(palette[state].name)) blocks.push({ position, state });
  });

  if (blocks.length === 0) throw new StructureError('表示できるブロックがありません。');
  return { name, format, size, palette, blocks, sourceBlockCount: raw.blocks.length };
}

export async function parseStructureNbt(buffer: ArrayBuffer, name?: string): Promise<StructureModel> {
  if (buffer.byteLength === 0) throw new StructureError('ファイルが空です。');
  if (buffer.byteLength > MAX_FILE_BYTES) {
    throw new StructureError(`ファイルサイズは${MAX_FILE_BYTES / 1024 / 1024}MB以下にしてください。`);
  }

  try {
    const { parsed, type } = await parse(buffer);
    const raw = simplify(parsed);
    const model = isBedrockStructure(raw)
      ? normalizeBedrockStructure(raw, type, name)
      : normalizeStructure(raw, type, name);
    // こなや糸の向きは、まわりのブロックを見ないと決まらない。
    return resolveShapeConnections(resolveConnections(model));
  } catch (error) {
    // パーサが自分で出した説明はそのまま見せる。それ以外（ライブラリの例外など）だけ言い換える。
    if (error instanceof StructureError) throw error;
    throw new StructureError('NBTを読み込めませんでした。Java版の.nbt、または統合版・教育版の.mcstructureか確認してください。');
  }
}
